import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const services = [
  {
    id: "01",
    slug: "procedural-security",
    title: "Procedural Security Solutions",
    description: "Physical and Armed Guarding, Tactical Intervention, Risk Mitigation, and uncompromising Executive Protection.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/jwzlew0c_Untitled-1.webp"
  },
  {
    id: "02",
    slug: "security-technology",
    title: "Security Technology Solutions",
    description: "Integrated CCTV, Off-site Monitoring, Access Control Scanners, and Body Cameras providing full visibility.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/b7ek2kq7_IMG_20161104_132118.webp"
  },
  {
    id: "03",
    slug: "mobile-app",
    title: "Mobile App & Web Platform",
    description: "Real-time reporting, Geo-location panic buttons, Electronic Occurrence Books, and instant connectivity.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/h4hao9an_Two%20Screen%20Phone%20App%20Preview%202025.webp"
  },
  {
    id: "04",
    slug: "methodology",
    title: "Management & Methodology",
    description: "Our core pillars: Predict, Prevent, Respond, Manage. Ensuring procedural compliance and total transparency through quality management systems and real-time reporting tools for clients.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/1ybm1fua_Management%20Team.png"
  },
  {
    id: "05",
    slug: "specialised-services",
    title: "Specialised Services",
    description: "Tailored security solutions including armed escorts, intelligence gathering, and specialized riot response teams.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/pejzbb52_tacticaL.webp"
  }
];

const Services = () => {
  return (
    <section className="py-32 bg-[#050505] relative z-10" id="services">
      <div className="container mx-auto px-6">
        <div className="mb-32 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <h2 className="text-[#FFD700] font-space-grotesk tracking-widest text-sm uppercase mb-4 flex items-center gap-4">
              <span className="w-12 h-[1px] bg-[#FFD700]"></span>
              Our Manifesto
            </h2>
            <h3 className="text-4xl md:text-6xl font-space-grotesk font-bold uppercase tracking-tight max-w-2xl">
              Solutions that <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-400 to-gray-600">defy convention.</span>
            </h3>
          </div>
          <p className="text-gray-400 max-w-sm text-lg font-light pb-2">
            We don't just secure spaces; we engineer environments of absolute certainty through technology and methodology.
          </p>
        </div>

        <div className="flex flex-col gap-32 md:gap-48">
          {services.map((service, idx) => (
            <ServiceRow key={service.id} service={service} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceRow = ({ service, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["0 1", "0.5 0.5"]
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0.2, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [100, 0]);

  return (
    <motion.div 
      ref={ref}
      style={{ opacity, y }}
      className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 md:gap-24`}
    >
      <div className="w-full md:w-1/2 relative group">
        <div className="absolute inset-0 bg-[#FFD700] rounded-xl transform translate-x-4 translate-y-4 opacity-20 transition-transform duration-500 group-hover:translate-x-6 group-hover:translate-y-6"></div>
        <div className="relative aspect-[4/3] rounded-xl overflow-hidden clip-path-polygon">
          <img 
            src={service.image} 
            alt={service.title} 
            className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
        </div>
      </div>
      
      <div className="w-full md:w-1/2 flex flex-col justify-center relative">
        <span className="text-[#FFD700] text-7xl md:text-9xl font-bold font-space-grotesk opacity-[0.08] mb-[-2rem] md:mb-[-4rem] ml-[-1rem] pointer-events-none select-none">
          {service.id}
        </span>
        <h4 className="text-3xl md:text-5xl font-space-grotesk font-bold uppercase tracking-tight mb-6 relative z-10">
          {service.title}
        </h4>
        <p className="text-xl text-gray-400 font-light mb-10 max-w-lg leading-relaxed">
          {service.description}
        </p>
        
        <Link 
          to={`/solutions/${service.slug}`}
          className="flex items-center gap-4 text-sm font-space-grotesk uppercase tracking-widest text-white hover:text-[#FFD700] transition-colors w-fit group"
        >
          Explore Solution
          <span className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:border-[#FFD700] group-hover:bg-[#FFD700]/10 transition-all">
            <ArrowUpRight size={16} className="transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </span>
        </Link>
      </div>
    </motion.div>
  );
};

export default Services;
